/**
 * Display helpers for resolving a pipeline_id (UUID) into a label + hex color
 * for rendering in cards, dropdowns, timelines, etc.
 *
 * After the kind-column migration, the legacy `pipeline_stage` /
 * `from_stage` / `to_stage` text columns are gone — every reference to a
 * pipeline goes through `pipeline_id`. System-pipeline detection (closed
 * won/lost) uses the stable `kind` column instead of matching the title.
 */
import type { PipelineKind, PipelineRow } from "@/types/pipeline";

/** Fallback color when a pipeline can't be resolved (e.g. NULL FK). */
export const FALLBACK_STAGE_COLOR = "#8a93a3";

export interface StageDisplay {
  label: string;
  color: string;
}

/**
 * Resolve a pipeline_id to its display label + hex color. Returns "Unknown"
 * with the fallback color when the id is null or doesn't match any pipeline
 * in `pipelines` (e.g. a history row whose pipeline was deleted).
 */
export function resolveStageDisplay(
  pipelineId: string | null | undefined,
  pipelines: PipelineRow[],
): StageDisplay {
  if (!pipelineId) return { label: "Unknown", color: FALLBACK_STAGE_COLOR };
  const found = pipelines.find((p) => p.id === pipelineId);
  if (!found) return { label: "Unknown", color: FALLBACK_STAGE_COLOR };
  return { label: found.title, color: found.color ?? FALLBACK_STAGE_COLOR };
}

/** Find a system pipeline by its stable kind. */
export function findPipelineByKind(
  pipelines: PipelineRow[],
  kind: PipelineKind,
): PipelineRow | undefined {
  return pipelines.find((p) => p.kind === kind);
}

/** The workspace's Closed Won pipeline, if it exists. */
export function findClosedWonPipeline(
  pipelines: PipelineRow[],
): PipelineRow | undefined {
  return findPipelineByKind(pipelines, "closed_won");
}

/** The workspace's Closed Lost pipeline, if it exists. */
export function findClosedLostPipeline(
  pipelines: PipelineRow[],
): PipelineRow | undefined {
  return findPipelineByKind(pipelines, "closed_lost");
}

/** The workspace's Watchlist pipeline, if it exists. */
export function findWatchlistPipeline(
  pipelines: PipelineRow[],
): PipelineRow | undefined {
  return findPipelineByKind(pipelines, "watchlist");
}

/** Closed Won / Closed Lost are terminal — excluded from "active value" and
 *  "avg time in stage" aggregations. */
export function isTerminalPipeline(p: PipelineRow): boolean {
  return p.kind === "closed_won" || p.kind === "closed_lost";
}

/** System pipelines users may not delete. Closed Lost is intentionally
 *  excluded — workspaces that don't track lost deals may remove it. */
export const PROTECTED_PIPELINE_KINDS: readonly PipelineKind[] = [
  "watchlist",
  "closed_won",
] as const;

/** Whether the given pipeline is protected from deletion (by stable `kind`). */
export function isProtectedPipeline(p: PipelineRow): boolean {
  return p.kind !== null && (PROTECTED_PIPELINE_KINDS as readonly string[]).includes(p.kind);
}

/** Whether a pipeline_id refers to a terminal pipeline. */
export function isTerminalStage(
  pipelineId: string | null | undefined,
  pipelines: PipelineRow[],
): boolean {
  if (!pipelineId) return false;
  const match = pipelines.find((p) => p.id === pipelineId);
  return match ? isTerminalPipeline(match) : false;
}
